import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import LoginPage from './components/Auth/LoginPage';
import AuthGuard from './guards/AuthGuard';
import AdminGuard from './guards/AdminGuard';
import TopSideBars from './components/Bars/TopSideBars';
import Dashboard from './pages/Dashboard/Dashboard';
import { NewClientOrder } from './pages/ClientQuotes/NewClientOrder';
import Inventory from './pages/Inventory/Inventory';
import { FutureOrders } from './pages/FutureOrder/FutureOrders';
import { AddNewFutureOrder } from './pages/FutureOrder/AddNewFutureOrder/AddNewFutureOrder';
import { BalanceFlowScreen } from './pages/BalanceFlow/BalanceFlowScreen';
import ProductOverview from './pages/Policy/ProductOverview/ProductOverview';
import { MaterialDetails } from '@pages/Policy/MaterialDetails/MaterialDetails';
import { Strategies } from './pages/Strategy/Strategies';
import { AddNewProduct } from './pages/ProductSettings/AddNewProduct';
import { EditRouteDetailsScreen } from './pages/Policy/RouteDetails/EditRouteDetailsSreen/EditRouteDetailsScreen';
import { ProductDashboard } from './pages/ProductSettings/ProductDashboard/ProductDashboard';
import { ProductDashboardProductSummary } from './pages/ProductSettings/ProductDashboard/ProductDashboardProductSummary';
import { FinancialPositions } from './pages/FinancialPositions/FinancialPositions';
import { Opportunity } from './pages/Opportunity/Opportunity';
import { OpportunityDashboard } from './pages/Opportunity-dashboard/OpportunityDashboard';
import { ExposureOverview } from './pages/ExposureOverview/ExposureOverview';
import { AdminPage } from './pages/AdminChanges/AdminPage';
import { UploadManageFilesWrapper } from './pages/UploadManageFiles/UploadManageFiles';
import { HistoricalPrices } from './pages/HistoricalPrices/HistoricalPrices';
import { NotificationRulesPage } from './pages/NotificationRules/NotificationRulesPage';
import { Demand } from './pages/FutureOrder/Demand';
import { TaskManager } from './pages/TaskManage/TaskManager';
import { UsersManagement } from './pages/Admin/UsersManagement';
import AccountsManagement from './pages/Admin/AccountsManagement';
import { DataFetch } from './pages/Admin/DataFetch';
import ContractsManagement from './pages/Admin/ContractsManagement';
import VMManagementPage from './pages/Admin/VMManagementPage';
import ImportBatches from './pages/Admin/ImportBatches';
import MaterialsPerAccount from './pages/Admin/MaterialsPerAccount';
import VolatilityRisk from './pages/Admin/VolatilityRisk';
import CronJobs from './pages/Admin/CronJobs';
import AdminDashboard from './pages/Admin/AdminDashboard';
import MaterialMappings from './pages/Admin/MaterialMappings';
import AccountSnapshots from './pages/Admin/AccountSnapshots';
import EmailIngestions from './pages/Admin/EmailIngestions';
import ActivityTimeline from './pages/Activity/ActivityTimeline';

const Router = () => {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" />} />
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/registration"
        element={
          <AuthGuard>
            <LoginPage loginComponent={'Password'} />
          </AuthGuard>
        }
      />

      <Route
        path="/dashboard/exposure-overview"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Dashboard - Exposure overview">
              <ExposureOverview />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/dashboard"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Dashboard">
              <Dashboard />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/Supply"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Supply">
              <Inventory />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/client-order"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Quotes">
              <NewClientOrder />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/demand"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Demand">
              <Demand />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/future-order/"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Demand">
              <Demand />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/add-future-order/:orderId/*"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Demand">
              <AddNewFutureOrder />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/sales-commitments"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Sales commitments">
              <Demand />
            </TopSideBars>
          </AuthGuard>
        }
      />

      <Route
        path="/balance-risk/:balanceID/*"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Contracts">
              <BalanceFlowScreen key={useParams().balanceID} />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/admin-change-data/*"
        element={
          <AuthGuard>
            <AdminGuard>
              <TopSideBars currentItem="Admin Data">
                <AdminPage />
              </TopSideBars>
            </AdminGuard>
          </AuthGuard>
        }
      />
      <Route
        path="/admin"
        element={
          <AuthGuard>
            <AdminGuard>
              <TopSideBars currentItem="Admin">
                <AdminDashboard />
              </TopSideBars>
            </AdminGuard>
          </AuthGuard>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AuthGuard>
            <AdminGuard>
              <TopSideBars currentItem="Admin">
                <UsersManagement />
              </TopSideBars>
            </AdminGuard>
          </AuthGuard>
        }
      />
      <Route
        path="/admin/accounts"
        element={
          <AuthGuard>
            <AdminGuard>
              <TopSideBars currentItem="Admin">
                <AccountsManagement />
              </TopSideBars>
            </AdminGuard>
          </AuthGuard>
        }
      />
      <Route
        path="/admin/data-fetch"
        element={
          <AuthGuard>
            <AdminGuard allowedRoles={['superadmin']}>
              <TopSideBars currentItem="Admin">
                <DataFetch />
              </TopSideBars>
            </AdminGuard>
          </AuthGuard>
        }
      />
      <Route
        path="/admin/vm-management"
        element={
          <AuthGuard>
            <AdminGuard allowedRoles={['superadmin']}>
              <TopSideBars currentItem="Admin">
                <VMManagementPage />
              </TopSideBars>
            </AdminGuard>
          </AuthGuard>
        }
      />
      <Route
        path="/admin/imports"
        element={
          <AuthGuard>
            <AdminGuard allowedRoles={['superadmin']}>
              <TopSideBars currentItem="Admin">
                <ImportBatches />
              </TopSideBars>
            </AdminGuard>
          </AuthGuard>
        }
      />
      <Route
        path="/admin/materials"
        element={
          <AuthGuard>
            <AdminGuard allowedRoles={['superadmin']}>
              <TopSideBars currentItem="Admin">
                <MaterialsPerAccount />
              </TopSideBars>
            </AdminGuard>
          </AuthGuard>
        }
      />
      <Route
        path="/admin/volatility"
        element={
          <AuthGuard>
            <AdminGuard allowedRoles={['superadmin']}>
              <TopSideBars currentItem="Admin">
                <VolatilityRisk />
              </TopSideBars>
            </AdminGuard>
          </AuthGuard>
        }
      />
      <Route
        path="/admin/cron-jobs"
        element={
          <AuthGuard>
            <AdminGuard allowedRoles={['superadmin']}>
              <TopSideBars currentItem="Admin">
                <CronJobs />
              </TopSideBars>
            </AdminGuard>
          </AuthGuard>
        }
      />
      <Route
        path="/admin/material-mappings"
        element={
          <AuthGuard>
            <AdminGuard allowedRoles={['superadmin']}>
              <TopSideBars currentItem="Admin">
                <MaterialMappings />
              </TopSideBars>
            </AdminGuard>
          </AuthGuard>
        }
      />
      <Route
        path="/admin/email-ingestions"
        element={
          <AuthGuard>
            <AdminGuard allowedRoles={['superadmin']}>
              <TopSideBars currentItem="Admin">
                <EmailIngestions />
              </TopSideBars>
            </AdminGuard>
          </AuthGuard>
        }
      />
      <Route
        path="/admin/snapshots"
        element={
          <AuthGuard>
            <AdminGuard allowedRoles={['superadmin']}>
              <TopSideBars currentItem="Admin">
                <AccountSnapshots />
              </TopSideBars>
            </AdminGuard>
          </AuthGuard>
        }
      />
      <Route
        path="/admin/contracts"
        element={
          <AuthGuard>
            <AdminGuard>
              <TopSideBars currentItem="Admin">
                <ContractsManagement />
              </TopSideBars>
            </AdminGuard>
          </AuthGuard>
        }
      />
      <Route
        path="/policy"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Builder">
              <ProductOverview />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/setting-product-create/:productId"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Setting">
              <AddNewProduct />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/strategy"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Strategy & Insights">
              <Strategies />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/policy/material"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Policy">
              <MaterialDetails />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/change-route-details"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Route Page">
              <EditRouteDetailsScreen />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/products-dashboard"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Settings">
              <ProductDashboard />
            </TopSideBars>
          </AuthGuard>
        }
      ></Route>
      <Route
        path="/products-dashboard/:productId"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Settings">
              <ProductDashboardProductSummary />
            </TopSideBars>
          </AuthGuard>
        }
      />

      <Route
        path="/hedges"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Hedges">
              <FinancialPositions />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/hedges/:formulaId"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Hedges">
              <FinancialPositions />
            </TopSideBars>
          </AuthGuard>
        }
      />

      {/* Redirect old URL to new one */}
      <Route path="/financial-positions" element={<Navigate to="/hedges" replace />} />
      <Route path="/financial-positions/:materialId" element={<Navigate to="/hedges/:materialId" replace />} />

      <Route
        path="/opportunity"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Routes optimization">
              <Opportunity />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/historical-prices"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Historical prices">
              <HistoricalPrices />
            </TopSideBars>
          </AuthGuard>
        }
      >
        <Route path=":formulaId" element={<HistoricalPrices />} />
      </Route>
      <Route
        path="/notifications"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Notifications">
              <NotificationRulesPage />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/activity"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Activity" hideFormulaBar>
              <ActivityTimeline />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/route-optimization"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Performance">
              <OpportunityDashboard />
            </TopSideBars>
          </AuthGuard>
        }
      />
      <Route
        path="/upload-files"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Upload Files">
              <UploadManageFilesWrapper />
            </TopSideBars>
          </AuthGuard>
        }
      >
        <Route path=":batchId" element={<UploadManageFilesWrapper />} />
      </Route>
      <Route
        path="/task-manager"
        element={
          <AuthGuard>
            <TopSideBars currentItem="Task Manager">
              <TaskManager />
            </TopSideBars>
          </AuthGuard>
        }
      />
    </Routes>
  );
};
export default Router;
